// assets/cart-api.js
// Cart helper for Shopify Horizon that keeps bundle children in sync with the main item.
// Uses the theme’s cart events and updates in as few requests as possible.
//
// Expected hidden DOM map:
// <ul class="product__bundle">
//   <li class="product__bundle-item" data-main-variant-id="12345">
//     <ul class="product__included">
//       <li data-variant-id="67890"></li>
//       <li data-variant-id="67891"></li>
//     </ul>
//   </li>
// </ul>

import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const POST_UPDATE_DELAY_MS = 20; // keep tiny to reduce UI delay, but avoid racing morph
let cartSyncTimer = null;
let syncing = false;

// ---------- ROUTES ----------
const CART_UPDATE_URL =
  window.Theme?.routes?.cart_update_url || '/cart/update.js';
const CART_CHANGE_URL =
  window.Theme?.routes?.cart_change_url || '/cart/change.js';
const CART_URL_JSON = window.Theme?.routes?.cart_url
  ? `${window.Theme.routes.cart_url}.js`
  : '/cart.js';
const SECTIONS_URL = () => window.location.pathname;

// ---------- SECTION HELPERS ----------
function collectCartSectionIds() {
  const s = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId)
      s.add(el.dataset.sectionId);
  });
  return Array.from(s);
}

function dispatchCartUpdate(sectionsObject) {
  const ids = collectCartSectionIds();
  let itemCount = 0;

  if (sectionsObject && ids.length) {
    const html = sectionsObject[ids[0]];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const txt =
        doc.querySelector('[ref="cartItemCount"]')?.textContent || '0';
      const n = parseInt(txt, 10);
      itemCount = Number.isFinite(n) ? n : 0;
    }
  }

  document.dispatchEvent(
    new CartUpdateEvent({}, ids[0] || 'cart', {
      itemCount,
      source: 'cart-api',
      sections: sectionsObject || {},
    })
  );
}

// ---------- UI HELPER ----------
function setSyncingUI(isOn) {
  document.querySelectorAll('cart-items-component').forEach((el) => {
    el.classList.toggle('is-syncing', !!isOn);
  });
}

// ---------- NETWORK ----------
/** POST /cart/update.js with a batch of variantId -> quantity (safe for unique/absent variants) */
async function updateCart(updates) {
  if (!updates || typeof updates !== 'object' || !Object.keys(updates).length)
    return null;

  setSyncingUI(true);
  try {
    const sections = collectCartSectionIds();
    const res = await fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: sections.join(','),
        sections_url: SECTIONS_URL(),
      }),
    });
    const txt = await res.text();
    let json = null;
    try {
      json = JSON.parse(txt);
    } catch {}
    if (json?.sections) dispatchCartUpdate(json.sections);
    return json;
  } finally {
    setSyncingUI(false);
  }
}

/** POST /cart/change.js per *line key* (needed for precise deletes / collapsing duplicates) */
async function changeCartByKeys(
  changes /* Array<{key:string, quantity:number}> */
) {
  if (!Array.isArray(changes) || changes.length === 0) return null;

  setSyncingUI(true);
  const sections = collectCartSectionIds();
  let lastJson = null;

  try {
    // Sequence > parallel to avoid cart races
    for (const ch of changes) {
      const res = await fetch(CART_CHANGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: ch.key,
          quantity: ch.quantity,
          sections: sections.join(','),
          sections_url: SECTIONS_URL(),
        }),
      });
      const txt = await res.text();
      try {
        lastJson = JSON.parse(txt);
      } catch {}
    }
    if (lastJson?.sections) {
      dispatchCartUpdate(lastJson.sections);
    } else {
      // Fallback ping
      dispatchCartUpdate(Object.fromEntries(sections.map((id) => [id, ''])));
    }
    return lastJson;
  } finally {
    setSyncingUI(false);
  }
}

/** GET /cart.js (current cart state) */
async function getCart() {
  const res = await fetch(CART_URL_JSON, {
    headers: { Accept: 'application/json' },
  });
  return /** @type {{ items: Array<{id:number, key:string, quantity:number, variant_id:number, properties?:object}>}} */ (
    await res.json()
  );
}

// ---------- BUNDLE MAP ----------
/** Build main→included map from the hidden DOM list (data-* based, robust) */
function getBundleMapFromDOM() {
  const map = new Map();
  const root = document.querySelector('ul.product__bundle');
  if (!root) return map;

  root.querySelectorAll('.product__bundle-item').forEach((li) => {
    const mainId = li.getAttribute('data-main-variant-id');
    if (!mainId) return;
    const includedIds = Array.from(
      li.querySelectorAll('.product__included [data-variant-id]')
    )
      .map((n) => n.getAttribute('data-variant-id'))
      .filter(Boolean)
      .map(String);
    map.set(String(mainId), includedIds);
  });

  return map;
}

// ---------- PUBLIC (direct) ----------
async function clearBundleByMainVariantId(mainVariantId) {
  const bundle = getBundleMapFromDOM();
  const children = bundle.get(String(mainVariantId)) || [];
  const updates = Object.fromEntries(
    [String(mainVariantId), ...children].map((id) => [id, 0])
  );
  return updateCart(updates);
}
async function setBundleQuantity(mainVariantId, quantity) {
  const bundle = getBundleMapFromDOM();
  const children = bundle.get(String(mainVariantId)) || [];
  const updates = Object.fromEntries(
    [String(mainVariantId), ...children].map((id) => [id, quantity])
  );
  return updateCart(updates);
}
async function removeByVariantId(variantId) {
  return updateCart({ [String(variantId)]: 0 });
}
async function setQuantity(variantId, quantity) {
  return updateCart({ [String(variantId)]: quantity });
}
/** Given a 1-based cart line index, get the line item from /cart.js */
async function getItemByLine(cartLine) {
  const cart = await getCart();
  return cart.items[cartLine - 1] || null;
}

// ---------- SYNC CORE ----------
/**
 * Compute the plan so every bundle child’s *total* quantity equals its main’s *total* quantity.
 * - If main missing => delete all children lines.
 * - If child present in multiple lines (different properties) => collapse to first line.
 */
function computeSyncPlan(cart, bundleMap) {
  const qtyByVariant = new Map(); // variant_id -> total qty
  const linesByVariant = new Map(); // variant_id -> line items[]

  for (const it of cart.items) {
    const vid = String(it.variant_id);
    qtyByVariant.set(vid, (qtyByVariant.get(vid) || 0) + it.quantity);
    if (!linesByVariant.has(vid)) linesByVariant.set(vid, []);
    linesByVariant.get(vid).push(it);
  }

  const variantUpdates = {}; // safe when unique/absent
  const keyUpdates = []; // precise deletes / collapsing

  for (const [mainId, children] of bundleMap.entries()) {
    const mainQty = qtyByVariant.get(String(mainId)) || 0;

    if (mainQty === 0) {
      // Main missing => ensure ALL children removed (by line key to be exact)
      for (const cid of children) {
        const childLines = linesByVariant.get(String(cid)) || [];
        for (const line of childLines) {
          if (line.quantity > 0)
            keyUpdates.push({ key: line.key, quantity: 0 });
        }
      }
      continue;
    }

    // Main present => each child total must equal mainQty
    for (const cid of children) {
      const childLines = linesByVariant.get(String(cid)) || [];
      const childTotal = qtyByVariant.get(String(cid)) || 0;

      if (childLines.length <= 1) {
        if (childTotal !== mainQty) variantUpdates[String(cid)] = mainQty;
      } else {
        // Collapse duplicates: first line -> mainQty; others -> 0
        for (let i = 0; i < childLines.length; i++) {
          const line = childLines[i];
          const newQty = i === 0 ? mainQty : 0;
          if (line.quantity !== newQty)
            keyUpdates.push({ key: line.key, quantity: newQty });
        }
      }
    }
  }

  const changed =
    Object.keys(variantUpdates).length > 0 || keyUpdates.length > 0;
  return { variantUpdates, keyUpdates, changed };
}

/** Apply plan (keys first for deletes/merges, then a single variant batch) */
async function applySyncPlan(plan) {
  const { keyUpdates, variantUpdates } = plan;
  if (keyUpdates.length) await changeCartByKeys(keyUpdates);
  if (Object.keys(variantUpdates).length) await updateCart(variantUpdates);
}

/** Run sync; returns true if it performed changes */
async function syncBundlesToMain() {
  if (syncing) return false;
  syncing = true;
  setSyncingUI(true);
  try {
    const bundle = getBundleMapFromDOM();
    if (bundle.size === 0) return false;

    const cart = await getCart();
    const plan = computeSyncPlan(cart, bundle);

    if (!plan.changed) return false;
    await applySyncPlan(plan);
    return true;
  } catch (e) {
    console.error('[CartAPI] syncBundlesToMain failed', e);
    return false;
  } finally {
    setSyncingUI(false);
    syncing = false;
  }
}

// ---------- EVENT WIRING ----------
// Fire sync *immediately after* the theme finishes its own change/morph.
// Keep the debounce extremely low to avoid racing its DOM morphing.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (cartSyncTimer) clearTimeout(cartSyncTimer);
  cartSyncTimer = setTimeout(syncBundlesToMain, POST_UPDATE_DELAY_MS);
});

// ---------- GLOBAL EXPOSE ----------
window.CartAPI = Object.freeze({
  // helpers
  getCart,
  getBundleMapFromDOM,

  // direct ops
  updateCart,
  changeCartByKeys,
  setQuantity,
  removeByVariantId,
  setBundleQuantity,
  clearBundleByMainVariantId,

  // sync
  syncBundlesToMain,
});
